/**
 * PendingTasksCard — compact "cosas por hacer" list for students.
 *
 * Renders on the Home page below Üben and below Kurs (see Dashboard.js)
 * so the student never misses an assignment. Hides itself completely when
 * there is nothing pending — the goal is a calm surface, not a to-do list
 * every day.
 *
 * Data: GET /api/student/pending-tasks (see submissions.py). The endpoint
 * returns { count, items: [{kind, id, title, category, link}, ...] }.
 * Teachers always receive count=0 (widget hides).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  ClipboardText,
  MicrophoneStage,
  Headphones,
  PencilLine,
  BookOpen,
  FileText,
  ArrowRight,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT_META = {
  speaking:          { icon: MicrophoneStage, color: '#AB1C24', label: { de: 'Sprechen',  es: 'Hablar'    } },
  listening:         { icon: Headphones,      color: '#D97706', label: { de: 'Hören',     es: 'Escuchar'  } },
  writing:           { icon: PencilLine,      color: '#8B5A3C', label: { de: 'Schreiben', es: 'Escribir'  } },
  reading_exercises: { icon: BookOpen,        color: '#2F6B50', label: { de: 'Lesen',     es: 'Leer'      } },
  reading:           { icon: BookOpen,        color: '#2F6B50', label: { de: 'Lesen',     es: 'Leer'      } },
  exercises:         { icon: FileText,        color: '#AB1C24', label: { de: 'Buch-Übg.', es: 'Ejercicios libro' } },
};

export default function PendingTasksCard({ isDe = true }) {
  const { user } = useAuth();
  const isTeacher = user?.role === 'admin' || user?.role === 'teacher';
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Teachers never see this widget — save a round-trip.
    if (isTeacher || !user) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/student/pending-tasks`, { withCredentials: true });
        if (!cancelled) setItems(res.data?.items || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [isTeacher, user]);

  if (isTeacher || !loaded || items.length === 0) return null;

  return (
    <div
      className="bg-gradient-to-br from-[#AB1C24]/8 via-white to-white border border-[#AB1C24]/25 rounded-xl px-4 py-3 mt-3"
      data-testid="pending-tasks-card"
    >
      <div className="flex items-center gap-2 mb-2">
        <ClipboardText size={16} weight="fill" className="text-[#AB1C24] flex-shrink-0" />
        <p className="text-sm font-bold text-[#292524]">
          {isDe ? 'Noch offen' : 'Pendiente por hacer'}
        </p>
        <span
          className="ml-auto text-[10px] font-bold bg-[#AB1C24] text-white px-2 py-0.5 rounded-full"
          data-testid="pending-tasks-count"
        >
          {items.length}
        </span>
      </div>
      <ul className="space-y-1">
        {items.slice(0, 5).map((it) => {
          const meta = CAT_META[it.category] || CAT_META[it.kind === 'reading_task' ? 'reading' : 'exercises'];
          const Icon = meta.icon;
          return (
            <li key={`${it.kind}-${it.id}`}>
              <Link
                to={it.link}
                className="group flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-[#AB1C24]/10 transition-colors"
                data-testid={`pending-task-${it.id}`}
              >
                <Icon size={14} weight="duotone" style={{ color: meta.color }} className="flex-shrink-0" />
                <span className="text-[11px] uppercase tracking-wider font-bold text-[#78716C] flex-shrink-0">
                  {meta.label[isDe ? 'de' : 'es']}
                </span>
                <span className="text-sm text-[#292524] truncate flex-1">{it.title}</span>
                <ArrowRight size={12} className="text-[#AB1C24] opacity-0 group-hover:opacity-100 flex-shrink-0" weight="bold" />
              </Link>
            </li>
          );
        })}
        {items.length > 5 && (
          <li className="text-[11px] text-[#78716C] pl-6 pt-1">
            {isDe ? `+ ${items.length - 5} weitere…` : `+ ${items.length - 5} más…`}
          </li>
        )}
      </ul>
    </div>
  );
}
